import moment from 'moment';
import Persistor from '../persistor/persistor';
import { DATE_FORMAT, formatDate } from '../date/utils';

// Must match the <Board id> in Components/CareNeed/CareNeedDashboard.js - react-kanban
// persists every board filter under `kanban.<boardId>.<filterKey>` and reads it back
// when the board mounts (see the filters in careNeedDashboard.js).
const BOARD_ID = 'CareNeedDashboard';
const filterKey = (key) => `kanban.${BOARD_ID}.${key}`;

/**
 * Sets the Einsatzboard's "Filtern nach" customer search, so the board opens on this
 * customer. Takes the name as shown on the board cards ("Nachname, Vorname" of the
 * display contact = customer.displayNames[0]). Like any filter there it stays set
 * until it's cleared on the board.
 */
export const showCustomerOnCareNeedDashboard = (customerName) =>
    Persistor.set(filterKey('customer'), customerName || '');

/**
 * Explains which of the Einsatzboard's own (persisted) filters would still hide this
 * customer's BK-Bedarf, so staff aren't left looking at an empty board. Location and
 * employee are filtered server side by the customer's location/employee
 * (CareNeedServiceImpl::filterResults), the date range by the care start - which, for
 * a BK-Bedarf without deployments, is its careFrom. Filters that were never set on
 * this browser are left out - they'd only be the board's defaults.
 *
 * @returns {string[]} one sentence per filter that hides it
 */
export const getCareNeedDashboardFilterWarnings = (customer, careNeed) => {
    const warnings = [];
    if (!customer) return warnings;

    const locations = Persistor.get(filterKey('location'));
    if (
        Array.isArray(locations) &&
        locations.length > 0 &&
        !locations.includes(customer.locationId)
    ) {
        warnings.push('Der Standort-Filter schließt den Standort dieses Kunden aus.');
    }

    const employees = Persistor.get(filterKey('employee'));
    if (
        Array.isArray(employees) &&
        employees.length > 0 &&
        !employees.includes(customer.employeeId)
    ) {
        warnings.push(
            'Der Mitarbeiter-Filter schließt den zuständigen Mitarbeiter dieses Kunden aus.'
        );
    }

    if (careNeed && careNeed.careFrom) {
        const from =
            Persistor.get(filterKey('from')) || moment().subtract(3, 'months').format(DATE_FORMAT);
        const till =
            Persistor.get(filterKey('till')) || moment().add(6, 'months').format(DATE_FORMAT);
        const start = moment(careNeed.careFrom);
        if (start.isBefore(moment(from), 'day') || start.isAfter(moment(till), 'day')) {
            warnings.push(
                `Der Zeitraum-Filter (${formatDate(from)} bis ${formatDate(
                    till
                )}) schließt den Beginn dieses BK-Bedarfs (${formatDate(careNeed.careFrom)}) aus.`
            );
        }
    }

    return warnings;
};
